import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, savingsMonthlyRequired } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

// Emergency fund calculator with personalized risk-multiplier.
// Output = recommended target + monthly savings to reach it.
export default function EmergencyFundPage() {
    const [monthlyExpenses, setMonthlyExpenses] = useState(4500);
    const [currentSavings, setCurrentSavings] = useState(2000);
    const [riskProfile, setRiskProfile] = useState("balanced"); // safe | balanced | risky
    const [hysaRate, setHysaRate] = useState(4.5);
    const [targetMonths, setTargetMonths] = useState(6);

    const result = useMemo(() => {
        const exp = toNum(monthlyExpenses);
        const cur = toNum(currentSavings);
        const months = toNum(targetMonths);
        // Risk multiplier: tweaks recommended months based on profile
        const profileMultiplier = riskProfile === "safe" ? 1.0
            : riskProfile === "balanced" ? 0.85
                : 0.7;
        const recommendedMonths = months * profileMultiplier;
        const target = exp * recommendedMonths;
        const gap = Math.max(0, target - cur);
        const monthlyAt12 = savingsMonthlyRequired(target, cur, toNum(hysaRate), 1);
        const monthlyAt24 = savingsMonthlyRequired(target, cur, toNum(hysaRate), 2);
        const monthlyAt36 = savingsMonthlyRequired(target, cur, toNum(hysaRate), 3);
        const status = cur >= target ? "complete"
            : cur >= target * 0.5 ? "halfway"
                : cur >= 1000 ? "starter"
                    : "critical";
        return {
            target,
            recommendedMonths,
            gap,
            monthlyAt12,
            monthlyAt24,
            monthlyAt36,
            currentMonths: exp > 0 ? cur / exp : 0,
            status,
        };
    }, [monthlyExpenses, currentSavings, riskProfile, hysaRate, targetMonths]);

    return (
        <CalculatorShell
            slug="emergency-fund"
            explainer={
                                <>
                    <h2>How this emergency fund calculator works</h2>
                    <p>
                        An emergency fund calculator tells you how large your liquid emergency reserve should
                        be and how long it will take to reach that target given your current savings and
                        monthly savings rate. Use it to set a concrete goal and track progress toward
                        financial security.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Target amount:</strong> monthly essential expenses × months of coverage — the standard recommendation is 3–6 months; 6–12 months for self-employed or variable income.</li>
                        <li><strong>Essential expenses:</strong> rent or mortgage, utilities, groceries, minimum debt payments, insurance premiums, and transportation — necessities only.</li>
                        <li><strong>Monthly savings needed:</strong> (target minus current savings) ÷ months to goal — straightforward once you've defined the target.</li>
                        <li><strong>Savings rate:</strong> determines your timeline to reaching the target; the calculator shows you both the amount and the estimated completion date.</li>
                        <li><strong>Account type:</strong> FDIC-insured, liquid accounts only — high-yield savings or money market accounts, not investments that can lose value.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Start with a $1,000 mini emergency fund as your first milestone — it covers most common unexpected expenses and builds the habit.</li>
                        <li>Keep your emergency fund at a separate institution from your checking account to reduce the temptation to spend it.</li>
                        <li>Replenish the fund immediately after any withdrawal — treat it as a recurring financial priority until it's back to target.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Monthly expenses" prefix="$" value={monthlyExpenses} onChange={setMonthlyExpenses} dataTestId="calc-input-expenses" hint="Rent + utilities + food + insurance + minimum debt payments" />
                    <Field label="Current emergency savings" prefix="$" value={currentSavings} onChange={setCurrentSavings} dataTestId="calc-input-current" />
                    <div>
                        <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-2">Risk profile</label>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { v: "risky", l: "Risky" },
                                { v: "balanced", l: "Balanced" },
                                { v: "safe", l: "Safe" },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    onClick={() => setRiskProfile(o.v)}
                                    data-testid={`risk-${o.v}`}
                                    className={`h-12 rounded-md border text-sm transition-colors ${
                                        riskProfile === o.v
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "border-border text-primary hover:border-primary/40"
                                    }`}
                                >
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Months target" value={targetMonths} onChange={setTargetMonths} dataTestId="calc-input-months" hint="Classic rule: 3–6" />
                        <Field label="Savings APY" suffix="%" value={hysaRate} onChange={setHysaRate} dataTestId="calc-input-apy" hint="HYSA yield" />
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Recommended target"
                        value={result.target}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: `Adjusted months (${result.recommendedMonths.toFixed(1)})`, value: result.recommendedMonths, format: (n) => `${n.toFixed(1)} mo`, testId: "calc-result-months" },
                            { label: "Currently saved", value: result.currentMonths, format: (n) => `${n.toFixed(1)} mo of expenses`, testId: "calc-result-current-months" },
                            { label: "Gap to target", value: result.gap, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-gap" },
                            { label: "To finish in 12 mo", value: result.monthlyAt12, format: (n) => `${fmtUSD(n, { decimals: 0 })}/mo`, testId: "calc-result-12mo" },
                            { label: "To finish in 24 mo", value: result.monthlyAt24, format: (n) => `${fmtUSD(n, { decimals: 0 })}/mo`, testId: "calc-result-24mo" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
